import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { IDownstreamUserChange } from '../types';
import type { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';

export enum FetchSource {
    Offline = 'offline',
    CommitSuccess = 'commitSuccess',
    LatestVersion = 'latestVersion',
    Transform = 'transform',
    Duplicate = 'duplicate',
}

export class UserChangeManager {
    private retryCount = 0;
    private userChangeCache: Map<string, Promise<IDownstreamUserChange>> = new Map();

    constructor(
        private submitHandler: {
            logger: ILogger;
            getRevisionConfig: () => { globalPadId: string; xsrf: string };
        },
        private moduleName: string,
    ) {}

    /**
     * 手动添加断层
     * @param userChange
     */
    public addUserChange(userChange: IDownstreamUserChange) {
        this.userChangeCache.set(this.getCacheKey(userChange.revision), Promise.resolve(userChange));
    }

    /**
     * 移除低于某个版本的断层
     * @param revision
     */
    public removeUserChangeBelowOrEqualVersion(revision: number) {
        const removeKeys: string[] = [];
        this.userChangeCache.forEach((_, key) => {
            const keyNum = +key.split(':')[1];
            if (keyNum <= revision) {
                removeKeys.push(key);
            }
        });
        removeKeys.forEach((key) => this.userChangeCache.delete(key));
    }

    /**
     * 获取缓存的断层
     * @param from
     * @param to
     * @returns
     */
    public async getUserChange(from: number, to: number, fetchSource: FetchSource) {
        this.submitHandler.logger.info(this.moduleName, `getUserChange from ${from} to ${to}`);
        this.requestUserChange(from, to, fetchSource);
        const requestPromise: Promise<IDownstreamUserChange>[] = [];
        for (let i = from; i <= to; i++) {
            const userChange = this.userChangeCache.get(this.getCacheKey(i));
            if (userChange) {
                requestPromise.push(userChange);
            } else {
                this.submitHandler.logger.error(this.moduleName, `getUserChange ${i} not found`);
                throw new Error(`getUserChange ${i} not found`);
            }
        }
        const group = await Promise.all(requestPromise);
        this.submitHandler.logger.info(this.moduleName, `success getUserChange from ${from} to ${to}`);
        return group.flat();
    }

    /**
     * 请求拉取断层
     * @param from
     * @param to
     * @param fetchSource
     */
    public requestUserChange(from: number, to: number, fetchSource: FetchSource) {
        const noCacheGroups: number[][] = [];
        let lastNoCacheRev = -99;
        for (let curRev = from; curRev <= to; curRev++) {
            if (this.userChangeCache.get(this.getCacheKey(curRev))) continue;
            if (lastNoCacheRev + 1 !== curRev) {
                noCacheGroups.push([curRev]);
            } else {
                const lastGroup = noCacheGroups[noCacheGroups.length - 1];
                lastGroup.push(curRev);
            }
            lastNoCacheRev = curRev;
        }
        noCacheGroups.forEach((group) => {
            this.requestFromServer(group[0], group[group.length - 1], fetchSource);
        });
    }

    /**
     * 从服务端拉取断层
     * @param from
     * @param to
     * @param fetchSource
     */
    private async requestFromServer(
        from: number,
        to: number,
        fetchSource: FetchSource,
        pendingPromise = this.createPendingPromise(),
    ): Promise<void> {
        this.submitHandler.logger.info(this.moduleName, `requestFromServer from ${from} to ${to || 'latest'}`);
        for (let i = from; i <= to; i++) {
            this.userChangeCache.set(
                this.getCacheKey(i),
                pendingPromise.promise.then((userChanges) => userChanges[i - from]),
            );
        }
        try {
            const userChanges = await this.fetchUserChanges(
                this.submitHandler.getRevisionConfig().globalPadId,
                from,
                to,
            );
            const continuous = this.checkContinuity(userChanges);
            if (!continuous) {
                this.submitHandler.logger.error(this.moduleName, 'requestFromServer res not continuous', userChanges);
                throw new Error('requestFromServer res not continuous');
            }
            pendingPromise.resolve(userChanges);
            this.submitHandler.logger.info(
                this.moduleName,
                `success requestFromServer from ${from} to ${to || 'latest'}`,
            );
            this.retryCount = 0;
        } catch (e) {
            this.retryRequestFromServer(from, to, fetchSource, pendingPromise, e);
        }
    }

    private async retryRequestFromServer(
        from: number,
        to: number,
        fetchSource: FetchSource,
        pendingPromise: ReturnType<typeof this.createPendingPromise>,
        e: any,
    ) {
        if (this.retryCount < 5 && navigator.onLine) {
            this.retryCount += 1;
            this.submitHandler.logger.info(this.moduleName, `retry requestFromServer, limit (${this.retryCount} / 5)`);
            await new Promise((resolve) => setTimeout(resolve, 1000 * this.retryCount));
            this.requestFromServer(from, to, fetchSource, pendingPromise);
            return;
        }
        this.submitHandler.logger.monitor?.reportStatus?.('fetchMissing', 1, e?.toString(), [
            fetchSource,
            `${navigator.onLine}`,
        ]);
        this.submitHandler.logger.error('requestFromServer failed exceed limit');
        pendingPromise.reject(e);
    }

    /**
     * 拉取缺失版本
     * @param globalPadId 文档id
     * @param from 开始的版本
     * @param to 结束版本，可不填
     * @param callback 回调
     */
    private async fetchUserChanges(globalPadId: string, from: number, to?: number) {
        if (to !== undefined && to < from) {
            this.submitHandler.logger.error(this.moduleName, `is inValid, to ${to} is less than from ${from}`);
            throw new Error('requestFromServer failed');
        }
        const toParam = to ? `&to=${to}` : '';
        const docsBaseUrl = location?.hostname ? `//${location.hostname}/` : '//docs.qq.com/';
        const res = await axios.get(
            `${docsBaseUrl}dop-api/get/changes?padId=${globalPadId}&from=${from}${toParam}&xsrf=${
                this.submitHandler.getRevisionConfig().xsrf
            }`,
            {
                timeout: 25000,
            },
        );
        return this.handleFetchChangesResponse(res, globalPadId, from, to);
    }

    /**
     * 处理断层结果
     * @param response
     * @param globalPadId
     * @param from
     * @param to
     * @returns
     */
    private async handleFetchChangesResponse(
        response: AxiosResponse,
        globalPadId: string,
        from: number,
        to?: number,
    ): Promise<IDownstreamUserChange[]> {
        const { data, status, statusText } = response || {};
        if (!data || status !== 200) {
            this.submitHandler.logger.error(
                this.moduleName,
                `${from} to ${to || 'latest'} response is ${statusText || 'void'}`,
            );
            throw new Error('requestFromServer failed');
        }
        if (data?.retcode !== 0) {
            // 100000 登录态校验失败
            // 100002 xsrf校验失败
            // 100003 后台内部错误
            // 200105 没有读取权限
            // 200200 后台数据拉取失败
            this.submitHandler.logger.error(
                this.moduleName,
                `${from} to ${to || 'latest'} retcode error, ${data.retcode}`,
            );
            throw Error('requestFromServer failed');
        }
        return this.handleFetchChangesSuccess(data, globalPadId, from, to);
    }

    private async handleFetchChangesSuccess(data: any, globalPadId: string, from: number, to?: number) {
        // to: 本次补拉拉到的最大版本号
        // from: 本地补拉开始的版本号
        // rev: 本次补拉的时候，后台的最大版本号
        const { rev, changes, to: end } = data.result;
        if (!changes.length) {
            this.submitHandler.logger.error(this.moduleName, `${from} to ${to || 'latest'} changes is empty`);
            throw Error('requestFromServer failed');
        }

        const missingChanges: IDownstreamUserChange[] = changes.map(
            (changeset: { data: { newRev: number; changeset: string; commitId: string } }) => {
                const change = changeset.data;
                const ret: IDownstreamUserChange = {
                    revision: change.newRev,
                    keyframe: change.changeset,
                    taskId: +change.commitId,
                };
                return ret;
            },
        );

        // 处理changesets
        if (to === undefined && rev > end) {
            // 没有指定结束参数 用后台吐的最大版本号参数rev来作为最大参数。 不能无限往上拉
            //  tempResult.push(...missingChanges);
            const changes = await this.fetchUserChanges(globalPadId, end + 1, rev);
            return missingChanges.concat(changes);
        }
        if (to !== undefined && to <= rev && to > end) {
            // 补拉完成后，发现还需要拉下一个分片
            const changes = await this.fetchUserChanges(globalPadId, end + 1, to);
            return missingChanges.concat(changes);
        }
        return missingChanges;
    }

    /**
     * 检查断层是否连续
     * @param userChanges
     * @returns
     */
    private checkContinuity(userChanges: IDownstreamUserChange[]) {
        if (!userChanges.length) return false;
        let prevRevision = userChanges[0].revision;
        return userChanges.slice(1).every((change) => {
            const { revision } = change;
            const isContinuity = revision === prevRevision + 1;
            prevRevision = revision;
            return isContinuity;
        });
    }

    /**
     * 创建一个pending状态的promise
     * @returns
     */
    private createPendingPromise() {
        let resolve: (userChanges: Array<IDownstreamUserChange>) => void = () => [];
        let reject: (e: any) => void = (e) => e;
        const promise = new Promise<Array<IDownstreamUserChange>>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    }

    private getCacheKey(revision: number) {
        return `${this.submitHandler.getRevisionConfig().globalPadId}:${revision}`;
    }
}
